'use client'

import React from 'react'
import { ChevronRight, Heart, DollarSign, Clock, MapPin, FileText } from 'lucide-react'

import type { ProjectCardModel } from '@/types/project-card-model'
import { Badge } from '@/components/ui/badge'
import { StatusTag, CategoryTag, TechTag } from '@/components/projects/ProjectTags'
import { calculateDaysUntilDue } from '@/utils/dashboard-status'

/**
 * =========================
 * Helpers
 * =========================
 */

const getWorkTypeLabel = (workType?: 'ONSITE' | 'REMOTE' | 'HYBRID' | null): string => {
  switch (workType) {
    case 'ONSITE':
      return '상주'
    case 'REMOTE':
      return '원격'
    case 'HYBRID':
      return '혼합'
    default:
      return '협의'
  }
}

const getStatusBadge = (createdAt: string, startDate?: string | null) => {
  const now = new Date()
  const created = new Date(createdAt)
  const daysSinceCreated = Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24))

  // NEW: 최근 7일
  if (daysSinceCreated <= 7) {
    return { type: 'new' as const, text: 'NEW' }
  }

  // 마감임박: start_date 3일 이내
  if (startDate) {
    const start = new Date(startDate)
    const daysUntilStart = Math.floor((start.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    if (daysUntilStart >= 0 && daysUntilStart <= 3) {
      return { type: 'closed' as const, text: `D-${daysUntilStart}` }
    }
  }

  return null
}

const normalizeText = (s?: string | null): string => (s ?? '').replace(/\s+/g, ' ').trim()

const safeTitle = (title?: string | null): string => {
  const t = normalizeText(title)
  return t.length >= 1 ? t : '제목 미정'
}

const truncateText = (text: string, maxLength: number): string | null => {
  const normalized = normalizeText(text)
  if (!normalized || normalized === '0') return null
  if (normalized.length <= maxLength) return normalized
  return normalized.slice(0, maxLength).trim() + '...'
}

const buildTagLine = (feilds?: string[] | null, skills?: string[] | null) => {
  const skillList = (skills ?? []).filter(Boolean)
  const shown = skillList.slice(0, 2)
  const rest = Math.max(0, skillList.length - shown.length)

  const firstFeild = feilds && feilds.length > 0 ? feilds[0] : null
  const hasSecondFeild = feilds && feilds.length > 1

  return { firstFeild, hasSecondFeild, shownSkills: shown, restCount: rest }
}

const formatDateDot = (iso?: string | null) => {
  if (!iso) return '날짜 없음'
  const d = new Date(iso)
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}.${month}.${day}`
}

const formatRelativeDays = (iso?: string | null) => {
  if (!iso) return '등록일 미정'
  const today = new Date()
  const created = new Date(iso)
  const diff = Math.floor((today.getTime() - created.getTime()) / (1000 * 60 * 60 * 24))
  if (diff <= 0) return '오늘 등록'
  return `등록 ${diff}일 전`
}

const formatKRW = (amount: number) =>
  new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW', maximumFractionDigits: 0 }).format(amount)

/**
 * =========================
 * Mode / Types
 * =========================
 */

type ProjectCardMode =
  | 'client'
  | 'freelancer'
  | 'clientMarket'
  | 'freelancerMarket'
  | 'companyMyProject'
  | 'assigned'

/**
 * ✅ assigned 모드에서만 추가로 필요한 필드 (AssignedProjectsClient에서 project에 얹어서 내려주기)
 * - ProjectCardModel 자체를 변경하지 않고, mode별로 타입을 강제하는 방식
 */
type AssignedExtras = {
  assignedStatus: 'INVITED' | 'ACTIVE' | 'COMPLETED' | 'LEFT'
  participationType: 'PERSONAL' | 'TEAM'
  teamName?: string | null
  myTeamRole?: 'MANAGER' | 'MAKER' | 'UNKNOWN'
  clientName?: string | null
  budgetLabel?: string | null
  dateLabel?: string | null
  myEstimateStatus?: string | null
  myEstimateTotalAmount?: number | null
}

/**
 * ✅ mode에 따라 project 타입이 달라지도록 “디스크리미네이티드 유니온”
 */
type BaseCommonProps = {
  onClick: () => void
  onBookmarkToggle?: () => void
  onEdit?: () => void
  onEstimateSubmit?: () => void
  onEstimateCountClick?: () => void
  onCloseProject?: () => void
  onReopenProject?: () => void
  onCompareEstimates?: () => void
  onManageMatching?: () => void
  onDuplicateProject?: () => void
  currentUserId?: string | null
}

type AssignedProps = BaseCommonProps & {
  mode: 'assigned'
  project: ProjectCardModel & AssignedExtras
}

type NonAssignedProps = BaseCommonProps & {
  mode: Exclude<ProjectCardMode, 'assigned'>
  project: ProjectCardModel
}

export type ProjectCardProps = AssignedProps | NonAssignedProps

/**
 * =========================
 * Card Shell (추천 방식)
 * - 카드 외곽 / hover / 클릭 처리만 담당
 * =========================
 */
function ProjectCardShell({
  onClick,
  children,
  className = '',
  isListMode = false,
}: {
  onClick: () => void
  children: React.ReactNode
  className?: string
  isListMode?: boolean
}) {
  if (isListMode) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onClick()
        }}
        className={['cursor-pointer hover:bg-gray-50 transition-colors', className].join(' ')}
      >
        {children}
      </div>
    )
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick()
      }}
      className={[
        'bg-white border border-gray-200 rounded-xl overflow-hidden cursor-pointer',
        'hover:border-gray-300 hover:shadow-sm transition-all',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  )
}

/**
 * =========================
 * ✅ Assigned Variant (새 모드)
 * =========================
 */

const ASSIGNED_STATUS_LABEL: Record<AssignedExtras['assignedStatus'], string> = {
  INVITED: '초대됨',
  ACTIVE: '진행중',
  COMPLETED: '완료',
  LEFT: '중단',
}

const ASSIGNED_STATUS_BADGE_CLASS: Record<AssignedExtras['assignedStatus'], string> = {
  INVITED: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  ACTIVE: 'bg-green-50 text-green-700 border-green-200',
  COMPLETED: 'bg-blue-50 text-blue-700 border-blue-200',
  LEFT: 'bg-gray-50 text-gray-700 border-gray-200',
}

function getTeamRoleLabel(role?: AssignedExtras['myTeamRole']) {
  if (role === 'MANAGER') return '매니저'
  if (role === 'MAKER') return '메이커'
  return '역할 미정'
}

function getEstimateStatusLabel(status?: string | null) {
  switch (status) {
    case 'ACCEPTED':
      return '수락됨'
    case 'SUBMITTED':
      return '검토중'
    case 'DRAFT':
      return '임시저장'
    case 'REJECTED':
      return '거절됨'
    default:
      return status ?? null
  }
}

function getEstimateStatusBadgeClass(status?: string | null) {
  switch (status) {
    case 'ACCEPTED':
      return 'bg-green-50 text-green-700 border-green-200'
    case 'SUBMITTED':
      return 'bg-blue-50 text-blue-700 border-blue-200'
    case 'DRAFT':
      return 'bg-yellow-50 text-yellow-700 border-yellow-200'
    case 'REJECTED':
      return 'bg-red-50 text-red-700 border-red-200'
    default:
      return 'bg-gray-50 text-gray-700 border-gray-200'
  }
}

function AssignedProjectCardContent({ project }: { project: ProjectCardModel & AssignedExtras }) {
  const title = safeTitle(project.title ?? null)

  const rawOutline =
    (typeof (project as any).description === 'string' && (project as any).description) ||
    (typeof (project as any).outline === 'string' && (project as any).outline) ||
    null

  const outline = rawOutline ? truncateText(rawOutline, 120) : null

  const statusText = ASSIGNED_STATUS_LABEL[project.assignedStatus]
  const statusClass = ASSIGNED_STATUS_BADGE_CLASS[project.assignedStatus]

  const metaParts = [project.clientName ?? null, project.budgetLabel ?? null, project.dateLabel ?? null].filter(
    Boolean,
  ) as string[]
  const metaText = metaParts.join(' · ')

  const isTeam = project.participationType === 'TEAM'

  const myEstimateLabel = getEstimateStatusLabel(project.myEstimateStatus)
  const myEstimateBadgeClass = getEstimateStatusBadgeClass(project.myEstimateStatus)

  return (
    <div className="p-5 flex flex-col" style={{ minHeight: 180 }}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 line-clamp-2 flex-1 min-w-0" title={title}>
          {title}
        </h3>
        <Badge className={`text-xs font-medium border shrink-0 ${statusClass}`}>{statusText}</Badge>
      </div>

      {/* Meta + Outline */}
      <div className="mt-3 space-y-2">
        {metaText && <p className="text-sm text-gray-700">{metaText}</p>}
        {outline && <p className="text-sm text-gray-600 leading-relaxed line-clamp-2">{outline}</p>}
      </div>

      {/* Participation */}
      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4">
        {isTeam ? (
          <>
            <Badge className="rounded-full bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1 text-xs font-medium">
              팀
            </Badge>

            {project.teamName ? (
              <Badge
                variant="outline"
                className="rounded-full border-blue-200 text-blue-700 px-3 py-1 text-xs font-medium"
              >
                {project.teamName}
              </Badge>
            ) : null}

            <Badge
              variant="outline"
              className="rounded-full border-gray-200 text-gray-700 px-3 py-1 text-xs font-medium"
            >
              {getTeamRoleLabel(project.myTeamRole)}
            </Badge>
          </>
        ) : (
          <Badge className="rounded-full bg-gray-100 text-gray-700 border border-gray-100 px-3 py-1 text-xs font-medium">
            개인
          </Badge>
        )}
      </div>

      {/* My estimate */}
      {(project.myEstimateStatus || typeof project.myEstimateTotalAmount === 'number') && (
        <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            {myEstimateLabel && (
              <Badge className={`text-xs font-medium border ${myEstimateBadgeClass}`}>{myEstimateLabel}</Badge>
            )}
            {project.myEstimateStatus === 'SUBMITTED' && <span className="text-xs text-gray-500">기업 확인 대기</span>}
          </div>

          {typeof project.myEstimateTotalAmount === 'number' && (
            <p className="text-sm text-gray-700">
              <span className="font-medium">총액</span>
              <span className="text-gray-400"> · </span>
              {formatKRW(project.myEstimateTotalAmount)}
            </p>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="mt-4 flex items-center justify-end">
        <span className="text-xs font-medium text-blue-600 hover:text-blue-700">상세 보기</span>
      </div>
    </div>
  )
}

/**
 * =========================
 * companyMyProject Variant (기존 로직 유지)
 * =========================
 */
function CompanyMyProjectCardContent({
  project,
  onEdit,
  onEstimateCountClick,
  onReopenProject,
  onCompareEstimates,
  onManageMatching,
  onDuplicateProject,
}: {
  project: ProjectCardModel
  onEdit?: () => void
  onEstimateCountClick?: () => void
  onReopenProject?: () => void
  onCompareEstimates?: () => void
  onManageMatching?: () => void
  onDuplicateProject?: () => void
}) {
  const p = project as any

  // D-Day 계산 및 상태 배지 생성
  const getStatusWithDDay = () => {
    // 마감된 프로젝트는 회색
    if (p.counselStatus === 'end') {
      return { 
        text: '마감됨', 
        className: 'text-gray-500 text-xs font-semibold',
        dDay: null 
      }
    }

    // dueDate 우선, 없으면 startDate 사용
    const deadlineDate = p.dueDate || p.startDate
    
    // 날짜가 없으면 배지를 표시하지 않음
    if (!deadlineDate) {
      return null
    }
    
    const daysUntilDue = calculateDaysUntilDue(deadlineDate)

    // 날짜 계산 실패
    if (daysUntilDue === null) {
      return null
    }

    // 이미 지난 경우: 지연 표시 (음수 허용)
    if (daysUntilDue < 0) {
      return {
        text: `D+${Math.abs(daysUntilDue)}`,
        className: 'text-red-600 text-xs font-semibold',
        dDay: daysUntilDue
      }
    }

    // D-1 이하: 빨간색 (긴급)
    if (daysUntilDue <= 1) {
      return {
        text: `D-${daysUntilDue}`,
        className: 'text-red-600 text-xs font-semibold',
        dDay: daysUntilDue
      }
    }

    // D-10 이상: 초록색 (안정)
    if (daysUntilDue >= 10) {
      return {
        text: `D-${daysUntilDue}`,
        className: 'text-green-600 text-xs font-semibold',
        dDay: daysUntilDue
      }
    }

    // D-2 ~ D-9: 주황색 (주의)
    return {
      text: `D-${daysUntilDue}`,
      className: 'text-orange-600 text-xs font-semibold',
      dDay: daysUntilDue
    }
  }

  const getPrimaryStatus = () => {
    if (p.counselStatus === 'end') return { text: '마감', className: 'text-gray-500 text-xs font-medium' }
    if (p.estimateStatus === 'accepted') return { text: '매칭 완료', className: 'text-green-600 text-xs font-medium' }
    if (p.estimateCount && p.estimateCount > 0) {
      return { text: `견적 ${p.estimateCount}개`, className: 'text-blue-600 text-xs font-medium' }
    }
    return { text: '견적 대기', className: 'text-gray-500 text-xs font-medium' }
  }

  const getPrimaryAction = () => {
    if (p.counselStatus === 'end') {
      if (onReopenProject) return { label: '재오픈', action: onReopenProject, variant: 'secondary' as const }
      if (onDuplicateProject) return { label: '프로젝트 복제', action: onDuplicateProject, variant: 'secondary' as const }
      return null
    }
    if (p.estimateStatus === 'accepted') {
      if (onManageMatching) return { label: '매칭 관리', action: onManageMatching, variant: 'default' as const }
      return null
    }
    if (p.estimateCount && p.estimateCount > 0) {
      if (onEstimateCountClick) return { label: '견적 보기', action: onEstimateCountClick, variant: 'default' as const }
      if (onCompareEstimates) return { label: '견적 비교하기', action: onCompareEstimates, variant: 'default' as const }
      return null
    }
    if (onEdit) return { label: '프로젝트 수정', action: onEdit, variant: 'outline' as const }
    return null
  }

  const statusWithDDay = getStatusWithDDay()
  const primaryStatus = getPrimaryStatus()
  const primaryAction = getPrimaryAction()

  const displayTitle = safeTitle(p.title)
  const descriptionText = p.description ? truncateText(p.description, 100) : null

  const metaParts: string[] = []
  if (p.cost) metaParts.push(p.cost)
  if (p.period) metaParts.push(p.period)
  if (p.workType) metaParts.push(getWorkTypeLabel(p.workType))
  const metaText = metaParts.join(' · ')

  const tagLine = buildTagLine(p.feilds, p.skills)
  const formattedDate = formatDateDot(p.createdAt)
  const relativeDate = formatRelativeDays(p.createdAt)

  // 상태 중심 리스트 형태로 단순화
  return (
    <div className="px-3 sm:px-4 py-3 flex flex-wrap items-center gap-2 sm:gap-4">
      {/* 좌측: D-Day 배지 (작게) */}
      {statusWithDDay && (
        <span className={`${statusWithDDay.className} shrink-0 w-10 sm:w-14 text-center text-xs`}>
          {statusWithDDay.text}
        </span>
      )}

      {/* 중앙: 제목과 메타 정보 */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 flex-wrap">
          <h3 className="font-semibold text-sm sm:text-base text-gray-900 truncate" title={displayTitle}>
            {displayTitle}
          </h3>
          {primaryStatus && (
            <span className={`${primaryStatus.className} shrink-0 whitespace-nowrap`}>
              {primaryStatus.text}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-gray-500 flex-wrap">
          {metaText && <span className="whitespace-nowrap">{metaText}</span>}
          {tagLine.firstFeild && <span className="whitespace-nowrap">· {tagLine.firstFeild}</span>}
          <span className="whitespace-nowrap">· {formattedDate}</span>
        </div>
      </div>

      {/* 우측: 액션 버튼 (최소화) */}
      {primaryAction && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            primaryAction.action()
          }}
          className={`px-2 sm:px-4 py-1.5 sm:py-2 h-8 sm:h-9 text-xs sm:text-sm font-medium rounded-lg transition-colors shrink-0 whitespace-nowrap ${
            primaryAction.variant === 'default'
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          <span className="hidden sm:inline">{primaryAction.label}</span>
          <span className="sm:hidden">보기</span>
        </button>
      )}
    </div>
  )
}

/**
 * =========================
 * Market Variant (clientMarket / freelancerMarket)
 * =========================
 */
function MarketProjectCardContent({
  project,
  mode,
  onBookmarkToggle,
}: {
  project: ProjectCardModel
  mode: 'clientMarket' | 'freelancerMarket'
  onBookmarkToggle?: () => void
}) {
  const p = project as any
  const displayTitle = safeTitle(p.title)
  const statusBadge = p.createdAt ? getStatusBadge(p.createdAt, p.startDate) : null
  const descriptionText = p.description ? truncateText(p.description, 120) : null
  const tagLine = buildTagLine(p.feilds, p.skills)

  const metaParts: string[] = []
  if (p.cost) metaParts.push(p.cost)
  if (p.period) metaParts.push(p.period)
  if (p.workType) metaParts.push(getWorkTypeLabel(p.workType))
  const metaText = metaParts.join(' · ')

  const formattedDate = formatDateDot(p.createdAt)
  const relativeDate = formatRelativeDays(p.createdAt)
  const isBookmarked = Boolean(p.isBookmarked)

  const myEstimateBadge = (() => {
    if (mode !== 'freelancerMarket') return null
    const status = p.myEstimateStatus
    if (!status) return null
    switch (status) {
      case 'DRAFT':
        return { text: '임시저장', variant: 'draft' as const }
      case 'SUBMITTED':
        return { text: '제출함', variant: 'submitted' as const }
      case 'ACCEPTED':
        return { text: '수락', variant: 'accepted' as const }
      case 'REJECTED':
        return { text: '거절', variant: 'rejected' as const }
      case 'WITHDRAWN':
        return null
      default:
        return null
    }
  })()

  return (
    <div className="p-5 flex flex-col" style={{ minHeight: 200 }}>
      <div className="flex flex-col gap-2 mb-3">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-semibold text-lg text-gray-900 leading-tight line-clamp-2 flex-1 min-w-0" title={displayTitle}>
            {displayTitle}
          </h3>

          <div className="flex items-center gap-2 flex-shrink-0">
            {statusBadge && (
              <StatusTag variant={statusBadge.type}>{statusBadge.text}</StatusTag>
            )}

            {onBookmarkToggle && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onBookmarkToggle()
                }}
                className={`p-1.5 rounded transition-colors ${
                  isBookmarked ? 'text-red-500 hover:bg-red-50' : 'text-gray-400 hover:text-red-500 hover:bg-gray-50'
                }`}
                title={isBookmarked ? '북마크 해제' : '북마크 추가'}
              >
                <Heart className={`w-4 h-4 ${isBookmarked ? 'fill-current' : ''}`} />
              </button>
            )}
          </div>
        </div>
      </div>

      {descriptionText && (
        <div className="mb-3">
          <p className="text-sm text-gray-600 leading-relaxed line-clamp-2">{descriptionText}</p>
        </div>
      )}

      {metaText && (
        <div className="mb-3">
          <p className="text-sm text-gray-700 font-medium">{metaText}</p>
        </div>
      )}

      {(tagLine.firstFeild || tagLine.shownSkills.length > 0 || myEstimateBadge) && (
        <div className="mb-4 flex items-center gap-2 overflow-hidden">
          <div className="flex items-center gap-1.5 flex-nowrap min-w-0">
            {tagLine.firstFeild && (
              <CategoryTag>
                {tagLine.firstFeild}
                {tagLine.hasSecondFeild && <span className="ml-1 text-gray-500">+1</span>}
              </CategoryTag>
            )}

            {tagLine.shownSkills.map((skill: string, idx: number) => (
              <TechTag key={idx}>{skill}</TechTag>
            ))}

            {tagLine.restCount > 0 && (
              <span className="px-2 py-1 text-xs font-medium text-gray-500 whitespace-nowrap flex-shrink-0">
                +{tagLine.restCount}
              </span>
            )}

            {myEstimateBadge && <StatusTag variant={myEstimateBadge.variant}>{myEstimateBadge.text}</StatusTag>}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100 mt-auto">
        <span className="text-xs text-gray-500 whitespace-nowrap" title={`등록: ${formattedDate}`}>
          {relativeDate}
        </span>
        {mode === 'freelancerMarket' ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 text-xs font-medium hover:bg-gray-50"
            >
              상세보기
            </button>
            <button
              type="button"
              className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700"
            >
              견적 보내기
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1 text-blue-600 font-medium text-sm hover:text-blue-700 transition-colors">
            <span>상세 보기</span>
            <ChevronRight className="w-4 h-4" />
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * =========================
 * Default Variant (client / freelancer)
 * =========================
 */
const deriveStatusInfo = (status?: 'accepted' | 'pending' | 'none') => {
  switch (status) {
    case 'accepted':
      return { text: '매칭 완료', color: 'bg-blue-100 text-blue-800 border-blue-200' }
    case 'pending':
      return { text: '견적 도착', color: 'bg-blue-50 text-blue-700 border-blue-200' }
    case 'none':
    default:
      return { text: '견적 대기', color: 'bg-gray-50 text-gray-700 border-gray-200' }
  }
}

function DefaultProjectCardContent({
  project,
  mode,
  onClick,
  onBookmarkToggle,
  onEstimateSubmit,
  onEstimateCountClick,
  onCloseProject,
  onReopenProject,
}: {
  project: ProjectCardModel
  mode: 'client' | 'freelancer'
  onClick: () => void
  onBookmarkToggle?: () => void
  onEstimateSubmit?: () => void
  onEstimateCountClick?: () => void
  onCloseProject?: () => void
  onReopenProject?: () => void
}) {
  const p = project as any

  const showEstimateSummary = mode === 'client'
  const showBookmark = mode === 'freelancer'

  const statusInfo = deriveStatusInfo(p.estimateStatus)
  const isBookmarked = Boolean(p.isBookmarked)

  const isProjectEnded = p.counselStatus === 'end'
  const myEstimateStatus = p.myEstimateStatus
  const hasEstimate = Boolean(myEstimateStatus)

  const canWrite = !isProjectEnded && (!hasEstimate || myEstimateStatus === 'DRAFT')
  const canView = hasEstimate || isProjectEnded

  let ctaLabel = ''
  let ctaAction: (() => void) | null = null

  if (mode === 'freelancer' && onEstimateSubmit) {
    if (canWrite) {
      if (!hasEstimate) ctaLabel = '견적 작성하기'
      else if (myEstimateStatus === 'DRAFT') ctaLabel = '임시저장 이어서 작성'
      ctaAction = () => onEstimateSubmit()
    } else if (canView) {
      ctaLabel = myEstimateStatus === 'SUBMITTED' ? '제출한 견적 보기' : '견적 보기'
      ctaAction = () => onClick()
    }
  }

  const showPrimaryAction = mode === 'freelancer' && ctaAction !== null

  return (
    <div className="p-4 md:p-5 flex flex-col" style={{ minHeight: 220 }}>
      <div className="flex flex-col gap-3 flex-1">
        <div className="flex flex-col gap-2">
          <div className="flex items-start justify-between gap-2">
            <h3
              className="font-bold text-xl text-gray-900 leading-tight hover:text-blue-600 transition-colors flex-1 min-w-0 line-clamp-2"
              onClick={(e) => {
                e.stopPropagation()
                onClick()
              }}
              title={p.title}
            >
              {p.title}
            </h3>

            {showBookmark && onBookmarkToggle && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onBookmarkToggle()
                }}
                className={`p-2 rounded-lg transition-colors flex-shrink-0 ${
                  isBookmarked ? 'text-red-500 hover:bg-red-50' : 'text-gray-400 hover:text-red-500 hover:bg-gray-50'
                }`}
                title={isBookmarked ? '북마크 해제' : '북마크 추가'}
              >
                <Heart className={`w-5 h-5 ${isBookmarked ? 'fill-current' : ''}`} />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {mode === 'freelancer' && (
              <>
                {myEstimateStatus && (
                  <Badge
                    variant={
                      myEstimateStatus === 'ACCEPTED'
                        ? 'default'
                        : myEstimateStatus === 'SUBMITTED'
                        ? 'default'
                        : myEstimateStatus === 'DRAFT'
                        ? 'secondary'
                        : myEstimateStatus === 'REJECTED'
                        ? 'destructive'
                        : 'default'
                    }
                    className="text-xs whitespace-nowrap"
                  >
                    {myEstimateStatus === 'ACCEPTED'
                      ? '매칭 완료'
                      : myEstimateStatus === 'SUBMITTED'
                      ? '검토중'
                      : myEstimateStatus === 'DRAFT'
                      ? '임시저장'
                      : myEstimateStatus === 'REJECTED'
                      ? '거절됨'
                      : '견적 제출됨'}
                  </Badge>
                )}
                {isProjectEnded && !myEstimateStatus && (
                  <Badge variant="outline" className="text-xs whitespace-nowrap">
                    완료
                  </Badge>
                )}
              </>
            )}

            {showEstimateSummary && (
              <div className="flex items-center gap-2 flex-wrap">
                {(p.counselStatus === 'pending' || p.counselStatus === 'matched') && onCloseProject && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onCloseProject()
                    }}
                    className="px-4 py-1.5 text-sm font-medium text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg border border-gray-200 hover:border-red-300 transition-colors whitespace-nowrap"
                  >
                    마감 처리
                  </button>
                )}

                {p.counselStatus === 'end' && onReopenProject && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onReopenProject()
                    }}
                    className="px-4 py-1.5 text-sm font-medium text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors whitespace-nowrap"
                  >
                    마감 취소
                  </button>
                )}

                <div className={`px-4 py-1.5 text-sm font-medium rounded-lg border ${statusInfo.color} whitespace-nowrap`}>
                  {statusInfo.text}
                </div>
              </div>
            )}
          </div>

          {showEstimateSummary && p.estimateCount !== undefined && p.estimateCount > 0 && (
            <div
              onClick={(e) => {
                e.stopPropagation()
                onEstimateCountClick?.()
              }}
              className={`inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm font-semibold border border-blue-200 w-fit ${
                onEstimateCountClick ? 'cursor-pointer hover:bg-blue-100 hover:border-blue-300 transition-colors' : ''
              }`}
            >
              <FileText className="w-4 h-4 flex-shrink-0" />
              <span className="whitespace-nowrap">견적서 {p.estimateCount}개 제출됨</span>
            </div>
          )}
        </div>

        {/* Meta (아이콘 포함) */}
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2.5 flex-shrink-0">
            <div className="p-1.5 bg-blue-50 rounded-lg flex-shrink-0">
              <DollarSign className="w-4 h-4 text-blue-600" />
            </div>
            <div className="min-w-0">
              <span className="text-xs font-medium text-gray-500 block mb-0.5 whitespace-nowrap">예상 금액</span>
              <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">{p.cost || '미정'}</p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 flex-shrink-0">
            <div className="p-1.5 bg-blue-50 rounded-lg flex-shrink-0">
              <Clock className="w-4 h-4 text-blue-600" />
            </div>
            <div className="min-w-0">
              <span className="text-xs font-medium text-gray-500 block mb-0.5 whitespace-nowrap">예상 기간</span>
              <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">{p.period || '미정'}</p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 flex-shrink-0">
            <div className="p-1.5 bg-blue-50 rounded-lg flex-shrink-0">
              <MapPin className="w-4 h-4 text-blue-600" />
            </div>
            <div className="min-w-0">
              <span className="text-xs font-medium text-gray-500 block mb-0.5 whitespace-nowrap">근무 방식</span>
              <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">{getWorkTypeLabel(p.workType)}</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 pt-3 border-t border-gray-100 mt-auto">
          <span className="text-sm text-gray-500 whitespace-nowrap">
            등록일: {p.createdAt ? new Date(p.createdAt).toLocaleDateString('ko-KR') : '날짜 없음'}
          </span>

          <div className="flex items-center gap-2 flex-shrink-0">
            {showPrimaryAction && ctaAction && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  ctaAction()
                }}
                className={`text-xs sm:text-sm font-medium flex items-center justify-center gap-1 transition-colors px-3 sm:px-4 py-1.5 rounded-lg shadow-md whitespace-nowrap flex-shrink-0 ${
                  canWrite ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {ctaLabel}
              </button>
            )}

            <button
              onClick={(e) => {
                e.stopPropagation()
                onClick()
              }}
              className="text-xs sm:text-sm text-blue-600 font-medium hover:text-blue-700 flex items-center gap-1 transition-colors whitespace-nowrap flex-shrink-0"
            >
              <span>자세히 보기</span>
              <ChevronRight className="w-4 h-4 flex-shrink-0" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * =========================
 * Main Export
 * =========================
 */
export const ProjectCard: React.FC<ProjectCardProps> = (props) => {
  const { project, mode, onClick } = props

  // ✅ assigned
  if (mode === 'assigned') {
    return (
      <ProjectCardShell onClick={onClick}>
        <AssignedProjectCardContent project={project} />
      </ProjectCardShell>
    )
  }

  // ✅ companyMyProject - 리스트 형태로 단순화
  if (mode === 'companyMyProject') {
    return (
      <ProjectCardShell onClick={onClick} isListMode={true}>
        <CompanyMyProjectCardContent
          project={project}
          onEdit={props.onEdit}
          onEstimateCountClick={props.onEstimateCountClick}
          onReopenProject={props.onReopenProject}
          onCompareEstimates={props.onCompareEstimates}
          onManageMatching={props.onManageMatching}
          onDuplicateProject={props.onDuplicateProject}
        />
      </ProjectCardShell>
    )
  }

  // ✅ market
  if (mode === 'clientMarket' || mode === 'freelancerMarket') {
    return (
      <ProjectCardShell onClick={onClick}>
        <MarketProjectCardContent project={project} mode={mode} onBookmarkToggle={props.onBookmarkToggle} />
      </ProjectCardShell>
    )
  }

  // ✅ default
  return (
    <ProjectCardShell onClick={onClick}>
      <DefaultProjectCardContent
        project={project}
        mode={mode}
        onClick={onClick}
        onBookmarkToggle={props.onBookmarkToggle}
        onEstimateSubmit={props.onEstimateSubmit}
        onEstimateCountClick={props.onEstimateCountClick}
        onCloseProject={props.onCloseProject}
        onReopenProject={props.onReopenProject}
      />
    </ProjectCardShell>
  )
}
